import { Injectable } from '@angular/core';

export interface CookieOptions {
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private static DEFAULT_COOKIE_OPTIONS: CookieOptions = {
    path: '/',
    sameSite: 'Lax',
  };

  private readonly browserDocument: Document;

  // Inject the browser window for testability
  constructor(private readonly browserWindow: Window) {
    this.browserDocument = this.browserWindow.document;
  }

  /**
   * Retrieves a cookie value by name
   * @param name The name of the cookie to retrieve
   * @returns The cookie value if found, null otherwise
   */
  public getCookieValue(name: string): string | null {
    if (!name || !this.browserDocument.cookie) {
      return null;
    }

    // Escape special regex characters in the cookie name
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create regex pattern to match the cookie
    const regex = new RegExp(`(?:^|;)\\s*${escapedName}\\s*=\\s*([^;]*)`);
    const match = this.browserDocument.cookie.match(regex);

    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Sets a cookie with the specified parameters
   * @param name The name of the cookie
   * @param value The value to store in the cookie
   * @param expirationDate The expiration date for the cookie
   * @param options Additional cookie options
   */
  public setCookie(
    name: string,
    value: string,
    expirationDate: Date,
    options: CookieOptions = CookieService.DEFAULT_COOKIE_OPTIONS,
  ): void {
    if (!name || value === undefined || value === null) {
      return;
    }

    const cookieOptions = {
      ...CookieService.DEFAULT_COOKIE_OPTIONS,
      ...options,
    };
    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (expirationDate) {
      cookieString += `; expires=${expirationDate.toUTCString()}`;
    }

    if (cookieOptions.path) {
      cookieString += `; path=${cookieOptions.path}`;
    }

    if (cookieOptions.domain) {
      cookieString += `; domain=${cookieOptions.domain}`;
    }

    if (cookieOptions.secure) {
      cookieString += '; secure';
    }

    if (cookieOptions.sameSite) {
      cookieString += `; SameSite=${cookieOptions.sameSite}`;
    }

    this.browserDocument.cookie = cookieString;
  }
}
